import React, { useState, forwardRef, useRef, useEffect } from "react";
import Pagination from "@material-ui/lab/Pagination";
import TablePagination from "@material-ui/core/TablePagination";
import { Grid, Paper, SvgIcon, TableCell } from "@material-ui/core";
import pluralize from "pluralize";
import MaterialTable, {
  MTableToolbar,
  MTableBodyRow,
  MTableHeader,
} from "material-table";

import { SliderFilter, TableProps, TableTypes } from "./Table.types";
import Search from "../../assets/Table/Search.svg";
import TableActive from "../../assets/Table/TableActive.svg";
import TableInactive from "../../assets/Table/TableInactive.svg";
import GridActive from "../../assets/Table/GridActive.svg";
import GridInactive from "../../assets/Table/GridInactive.svg";
import TrashCan from "../../assets/Table/TrashCan.svg";
import { RangeSlider } from "./RangeSlider";
import { TableSelect } from "./TableSelect";
import { TableSelectMulti } from "./TableSelectMulti";
import { EllipsisMenu } from "./EllipsisMenu";
import { UserPermissions } from "../../enums";
import {
  Title,
  Container,
  TableContainer,
  ToolbarWrapper,
  useStyles,
  FilterContainer,
  DropDownSection,
  SliderSection,
  GridItemContainer,
  GridItemImage,
  GridItemContent,
  GridItemTextContainer,
  TextName,
  TextTitle,
  TableHeader,
  TitleContainer,
  Count,
  ViewIcon,
  ViewContainer,
  tableStyles,
  headerStyle,
  getSearchFieldStyle,
  Remove,
  RemoveContainer,
  AddMembersButton,
  Button,
  EllipsisContainer,
} from "./Table.styles";
import "./Table.scss";
import { MemberInfo } from "@milestone/component-library/dist/MemberModal/MemberModal.types";
import { useMemberDetails } from "../../services/hooks/MemberDetails";
import { MemberModal } from "@milestone/component-library";
import { useAppSelector } from "../../store/hooks";

export const Table: React.FC<TableProps> = ({
  title,
  columns,
  data,
  tableWidth = "",
  searchable,
  defaultRows,
  rowsPerPage,
  isLoading,
  tableType,
  sliderFilters,
  dropDownFilters,
  multiDropDownFilters,
  selectedRows,
  handleDeleteSelected,
  editable,
  handleAddButton,
  handleAddTeamMember,
}) => {
  const token = useAppSelector(state => state.auth.token);
  const userPermissions = useAppSelector(state => state.auth.user.permissions);
  const classes = useStyles();
  const tableRef = useRef<any>();
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [viewType, toggleViewType] = useState<string>("table");
  const [showFilters, toggleFilters] = useState(false);

  const toggleView = (type: string) => {
    if (viewType === type) return;
    toggleViewType(type);
  };

  const toggleFilter = () => {
    toggleFilters(!showFilters);
  };

  const handleRowSelect = (selection: any) => {
    selectedRows.current = selection;
  };

  let detailSelect = undefined;
  if (tableType === TableTypes.Members || tableType === TableTypes.Dashboards)
    detailSelect = (
      event: React.MouseEvent | undefined,
      rowData: Record<string, any> | undefined
    ) => {
      if (event && rowData) {
        if ((event.target as HTMLElement).className !== "Ellipsis") {
          setPreviewId(rowData.id);
        }
      }
    };

  const handleChangePage = (
    e: React.ChangeEvent<unknown>,
    page: number,
    props: any
  ) => {
    props.onChangePage(e, page - 1);
    console.log("page change");
    if (tableHeaderRef.current)
      tableHeaderRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // member modal
  const [open, setOpen] = useState(false);
  const [member, setMember] = useState<MemberInfo>();
  const [previewId, setPreviewId] = useState("");
  const memberDetails = useMemberDetails(previewId);

  useEffect(() => {
    if (memberDetails) {
      setMember(memberDetails);
      setOpen(true);
    } else {
      setOpen(false);
      setPreviewId("");
    }
  }, [memberDetails]);

  const handleClose = (open = false) => {
    setOpen(open);
    setPreviewId("");
  };

  return (
    <Container>
      {userPermissions.includes(UserPermissions.MemberPreview) && (
        <MemberModal
          memberInfo={member}
          open={open}
          token={token}
          handleClose={handleClose}
        />
      )}
      {title && (
        <TableHeader ref={tableHeaderRef} tableWidth={tableWidth}>
          <TitleContainer>
            <Title>{title}</Title>
            <Count>{data.length}</Count>
          </TitleContainer>
          <ViewContainer>
            {tableType === TableTypes.Members ? (
              <>
                <ViewIcon
                  src={viewType === "grid" ? GridActive : GridInactive}
                  onClick={() => toggleView("grid")}
                />
                <ViewIcon
                  src={viewType === "table" ? TableActive : TableInactive}
                  onClick={() => toggleView("table")}
                />
              </>
            ) : tableType ? (
              <>
                <Button theme={"blue"} onClick={handleAddButton}>
                  + Add {pluralize.singular(tableType)}
                </Button>
              </>
            ) : null}
          </ViewContainer>
        </TableHeader>
      )}

      <TableContainer tableWidth={tableWidth} viewType={viewType}>
        <MaterialTable
          tableRef={tableRef}
          isLoading={isLoading}
          columns={columns}
          data={data}
          style={tableStyles}
          onSelectionChange={selection => handleRowSelect(selection)}
          onRowClick={detailSelect}
          options={{
            toolbar: true,
            search: searchable ? true : false,
            selection: editable && data.length > 0 ? true : false,
            showTextRowsSelected: false,
            showTitle: false,
            filtering: true,
            searchFieldAlignment: "left",
            pageSize: defaultRows,
            headerStyle: headerStyle,
            searchFieldStyle: getSearchFieldStyle(tableWidth),
            //actionsColumnIndex: -1,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: `Filter ${tableType || ""}`,
            },
            header: {
              actions: "",
            },
            body: {
              emptyDataSourceMessage: "",
            },
          }}
          icons={{
            Search: forwardRef<any>((props, ref) => (
              <img src={Search} {...props} ref={ref} />
            )),
            ResetSearch: forwardRef(() => null),
          }}
          components={{
            Container: props => (
              <>
                {viewType === "table" ? (
                  <div>
                    <Paper {...props} elevation={0} />
                  </div>
                ) : (
                  //id necessary for styling
                  <div id="gridViewContainer">
                    <Paper {...props} elevation={0} />
                  </div>
                )}
              </>
            ),
            Toolbar: props => (
              <ToolbarWrapper
                className={classes.toolbarWrapper}
                sliderFilters={sliderFilters}
                showFilters={showFilters}
                dropDownFilters={dropDownFilters}
                searchable={searchable}
                dataLength={data.length}
                editable={editable}
              >
                {searchable && (
                  <>
                    <MTableToolbar {...props} />
                    {sliderFilters ||
                    dropDownFilters ||
                    multiDropDownFilters ? (
                      <Button
                        theme={showFilters ? "blue" : "white"}
                        onClick={toggleFilter}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    ) : null}
                  </>
                )}
              </ToolbarWrapper>
            ),
            FilterRow: props => (
              <FilterContainer
                tableWidth={tableWidth}
                sliderFilters={sliderFilters}
                showFilters={showFilters}
                dropDownFilters={dropDownFilters}
                dataLength={data.length}
                editable={editable}
                searchable={searchable}
              >
                <>
                  <SliderSection>
                    {sliderFilters &&
                      sliderFilters.map(
                        (filter: SliderFilter, index: number) => (
                          <RangeSlider
                            {...props}
                            id={filter.id}
                            title={filter.title}
                            min={filter.min}
                            max={filter.max}
                            key={index}
                          />
                        )
                      )}
                  </SliderSection>
                  {dropDownFilters && (
                    <DropDownSection>
                      {dropDownFilters &&
                        dropDownFilters.map((filter: any, index: any) => (
                          <TableSelect
                            {...props}
                            id={filter.id}
                            title={filter.title}
                            options={filter.options}
                            filter={filter}
                            key={index}
                          />
                        ))}
                      {multiDropDownFilters &&
                        multiDropDownFilters.map((filter: any, index: any) => (
                          <TableSelectMulti
                            {...props}
                            id={filter.id}
                            title={filter.title}
                            options={filter.options}
                            key={index}
                          />
                        ))}
                    </DropDownSection>
                  )}
                </>
              </FilterContainer>
            ),
            Header: props => (
              <>
                {editable && handleAddTeamMember && (
                  <thead>
                    <tr>
                      <td>
                        <AddMembersButton
                          onClick={
                            handleAddTeamMember === "disabled"
                              ? null
                              : handleAddTeamMember
                          }
                          disabled={
                            handleAddTeamMember === "disabled" ? true : false
                          }
                        >
                          + Add
                          {tableType === TableTypes.Companies
                            ? " Office(s)"
                            : tableType === TableTypes.Rooms
                            ? " Room"
                            : " Team Member"}
                        </AddMembersButton>
                      </td>
                    </tr>
                  </thead>
                )}
                {editable && data.length > 0 && (
                  // id necessary for styling
                  <thead id="selectAll">
                    <tr>
                      <td>
                        <RemoveContainer onClick={handleDeleteSelected}>
                          <img src={TrashCan} />
                          <Remove>Remove</Remove>
                        </RemoveContainer>
                      </td>
                    </tr>
                  </thead>
                )}
                {viewType === "table" ? (
                  <MTableHeader {...props} />
                ) : (
                  // id necessary for styling
                  <thead id="gridViewHeader" />
                )}
              </>
            ),
            Row: props => (
              <>
                {viewType === "table" ? (
                  <MTableBodyRow {...props} />
                ) : (
                  <tr>
                    <td>
                      <GridItemContainer>
                        <GridItemImage
                          src={`${process.env.REACT_APP_API_URL}/members/${props.data.id}/photo?authorization=${token}`}
                        />
                        <GridItemContent>
                          <GridItemTextContainer>
                            <TextName>{props.data.fullName}</TextName>
                            <TextTitle>
                              {`${props.data.roleName} - ${props.data.teamName}`}
                            </TextTitle>
                          </GridItemTextContainer>
                          {tableType ? (
                            <EllipsisContainer>
                              <EllipsisMenu
                                gridView
                                rowData={props.data}
                                tableType={tableType}
                                setPreviewId={setPreviewId}
                              />
                            </EllipsisContainer>
                          ) : null}
                        </GridItemContent>
                      </GridItemContainer>
                    </td>
                  </tr>
                )}
              </>
            ),
            Action: props => (
              <>
                {tableType ? (
                  <EllipsisMenu
                    rowData={props.data}
                    tableType={tableType}
                    setPreviewId={setPreviewId}
                  />
                ) : null}
              </>
            ),
            Pagination: props => (
              <>
                {data.length >= 1 && (
                  <>
                    <TableCell>
                      <Grid>
                        <Pagination
                          count={
                            tableRef.current
                              ? Math.ceil(
                                  tableRef.current.state.data.length /
                                    props.rowsPerPage
                                )
                              : Math.ceil(data.length / props.rowsPerPage)
                          }
                          page={props.page + 1}
                          onChange={(e, page) =>
                            handleChangePage(e, page, props)
                          }
                          boundaryCount={1}
                          siblingCount={1}
                          variant="outlined"
                          shape="rounded"
                        />
                      </Grid>
                    </TableCell>
                    <TablePagination
                      page={props.page}
                      count={props.count}
                      onChangePage={props.onChangePage}
                      rowsPerPage={props.rowsPerPage}
                      onChangeRowsPerPage={props.onChangeRowsPerPage}
                      rowsPerPageOptions={rowsPerPage.map((num: number) => ({
                        value: num,
                        label: `${num} per page`,
                      }))}
                      SelectProps={{
                        IconComponent: (props: any) => (
                          <SvgIcon {...props}>
                            <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                          </SvgIcon>
                        ),
                      }}
                      classes={{
                        root: classes.paginationRoot,
                        caption: classes.paginationCaption,
                        select: classes.paginationSelect,
                        actions: classes.paginationActions,
                      }}
                    />
                  </>
                )}
              </>
            ),
          }}
          // necessary in order to override action component
          actions={[
            {
              icon: "lorem ipsum",
              tooltip: "lorem ipsum",
              position: "row",
              onClick: () => null,
            },
          ]}
        />
      </TableContainer>
    </Container>
  );
};
